import React, { useEffect, useRef, useState } from 'react';
import { X, Check, Copy, Phone, Mail, MessageSquare, ExternalLink, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { QuickHelpAccordion } from './QuickHelpAccordion';
import { useLanguage } from '@/hooks/useLanguage';

interface HelpSupportModalProps {
    isOpen: boolean;
    onClose: () => void;
    siteName?: string;
    supportNumber?: string;
    supportEmail?: string;
}

export function HelpSupportModal({
    isOpen,
    onClose,
    siteName = 'Vireon Pilot',
    supportNumber = '+91-9630042045',
    supportEmail = 'team@tech.vireontech.in',
}: HelpSupportModalProps) {
    const { t } = useLanguage();
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);

    // Track the element that had focus before opening the modal
    useEffect(() => {
        if (isOpen) {
            previousFocusRef.current = document.activeElement as HTMLElement;
            // Focus on close button initially (accessibility)
            setTimeout(() => {
                closeButtonRef.current?.focus();
            }, 50);
        } else if (previousFocusRef.current) {
            previousFocusRef.current.focus();
        }
    }, [isOpen]);

    // Handle ESC key press
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            if (e.key === 'Escape') {
                onClose();
            }

            // Focus trapping
            if (e.key === 'Tab' && modalRef.current) {
                const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey) {
                    // If shift + tab and on first element, cycle back to last
                    if (document.activeElement === firstElement) {
                        lastElement.focus();
                        e.preventDefault();
                    }
                } else {
                    // If tab and on last element, cycle back to first
                    if (document.activeElement === lastElement) {
                        firstElement.focus();
                        e.preventDefault();
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    // Trigger Toast Notification
    const showToast = (message: string) => {
        setToastMessage(message);
        setTimeout(() => {
            setToastMessage(null);
        }, 2500);
    };

    // Copy number to clipboard
    const handleCopyNumber = async (e: React.MouseEvent) => {
        e.preventDefault();
        try {
            await navigator.clipboard.writeText(supportNumber);
            showToast(t('Phone number copied to clipboard.'));
        } catch (err) {
            showToast(t('Failed to copy phone number.'));
        }
    };

    // WhatsApp Support Trigger
    const handleWhatsAppChat = () => {
        const waText = encodeURIComponent(
            `Hello Support Team,\n\nI need assistance regarding the ${siteName} Power Analysis Dashboard.\n\nThank you.`
        );
        window.open(`https://wa.me/919630042045?text=${waText}`, '_blank', 'noopener,noreferrer');
    };

    // Email Support Trigger
    const handleEmailSupport = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const subject = encodeURIComponent(`Cortex Support Request - ${siteName}`);
        const body = encodeURIComponent(
            `Support Request Context:\n` +
            `-----------------------------\n` +
            `Site Name: ${siteName}\n` +
            `Browser Information: ${navigator.userAgent}\n` +
            `Date & Time: ${new Date().toString()}\n` +
            `-----------------------------\n\n` +
            `Please describe the issue you are experiencing:\n`
        );
        window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(2px)' }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            {/* Toast popup message */}
            {toastMessage && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[110] rounded-xl bg-ink-900 px-4 py-2.5 text-xs font-semibold text-white shadow-xl flex items-center gap-2 animate-slide-up">
                    <Check className="h-4 w-4 text-brand-400 shrink-0" />
                    <span>{toastMessage}</span>
                </div>
            )}

            {/* Modal Card */}
            <div
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="help-support-title"
                className={cn(
                    'bg-white w-full border border-ink-100 flex flex-col justify-between overflow-hidden transition-all duration-300 animate-slide-up',
                    // Desktop sizing & animations
                    'sm:max-w-lg sm:rounded-2xl sm:max-h-[85vh] sm:shadow-xl',
                    // Mobile bottom sheet sizing
                    'max-sm:fixed max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:rounded-t-2xl max-sm:max-h-[85vh] max-sm:h-auto'
                )}
            >
                {/* Header */}
                <header className="flex items-center justify-between border-b border-ink-100 px-5 py-4">
                    <div className="flex items-center gap-2">
                        <HelpCircle className="h-5 w-5 text-brand-500" />
                        <h2 id="help-support-title" className="font-display text-base font-bold text-ink-900">
                            {t("Help & Support")}
                        </h2>
                    </div>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={onClose}
                        aria-label={t("Close Help and Support Modal")}
                        className="rounded-lg p-1.5 text-ink-400 hover:bg-ink-50 hover:text-ink-700 active:scale-95 transition"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </header>

                {/* Scrollable Content Container */}
                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5 no-scrollbar">
                    {/* Quick Accordion Section */}
                    <div>
                        <QuickHelpAccordion />
                    </div>

                    {/* Support Actions & Contact Info */}
                    <div className="space-y-3">
                        <h4 className="text-[10px] font-bold uppercase tracking-wider text-ink-400 px-1">
                            {t("Contact Tech Support")}
                        </h4>

                        <div className="grid grid-cols-1 gap-2.5">
                            {/* Call Card */}
                            <div className="rounded-xl border border-ink-100 bg-white p-3.5 flex items-start gap-3.5">
                                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-50 text-brand-500 shrink-0">
                                    <Phone className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h5 className="text-xs font-bold text-ink-900 leading-tight">{t("Call Support")}</h5>
                                    <p className="num text-[11px] text-ink-500 font-semibold mt-0.5">
                                        {supportNumber}
                                    </p>
                                    <p className="text-[10px] text-ink-400 mt-0.5">
                                        {t("Mon-Sat. 9:00 AM - 6:00 PM IST")}
                                    </p>

                                    <div className="flex items-center gap-2 mt-2">
                                        <a
                                            href={`tel:${supportNumber}`}
                                            className="inline-flex items-center gap-1 rounded-lg bg-brand-500 hover:bg-brand-600 active:scale-95 text-white px-2.5 py-1 text-[10px] font-bold transition shadow-sm"
                                        >
                                            {t("Call Now")}
                                        </a>
                                        <button
                                            type="button"
                                            onClick={handleCopyNumber}
                                            className="inline-flex items-center gap-1 rounded-lg border border-ink-200 hover:bg-ink-50 active:scale-95 text-ink-600 px-2.5 py-1 text-[10px] font-semibold transition"
                                        >
                                            <Copy className="w-3 h-3" />
                                            {t("Copy Number")}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Email Card */}
                            <div className="rounded-xl border border-ink-100 bg-white p-3.5 flex items-start gap-3.5">
                                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-50 text-brand-500 shrink-0">
                                    <Mail className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h5 className="text-xs font-bold text-ink-900 leading-tight">{t("Email Support")}</h5>
                                    <p className="text-[11px] text-ink-500 font-semibold mt-0.5">
                                        {supportEmail}
                                    </p>
                                    <p className="text-[10px] text-ink-400 mt-0.5">
                                        {t("Response time usually within 2 hours")}
                                    </p>

                                    <div className="mt-2">
                                        <a
                                            href={`mailto:${supportEmail}`}
                                            onClick={handleEmailSupport}
                                            className="inline-flex items-center gap-1 rounded-lg bg-brand-500 hover:bg-brand-600 active:scale-95 text-white px-2.5 py-1 text-[10px] font-bold transition shadow-sm"
                                        >
                                            {t("Send Email")}
                                        </a>
                                    </div>
                                </div>
                            </div>

                            {/* WhatsApp Card */}
                            <div className="rounded-xl border border-ink-100 bg-white p-3.5 flex items-start gap-3.5">
                                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-50 text-brand-500 shrink-0">
                                    <MessageSquare className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h5 className="text-xs font-bold text-ink-900 leading-tight">{t("WhatsApp Support")}</h5>
                                    <p className="text-[10px] text-ink-400 mt-0.5">
                                        {t("Get support directly on WhatsApp chat")}
                                    </p>

                                    <div className="mt-2">
                                        <button
                                            type="button"
                                            onClick={handleWhatsAppChat}
                                            className="inline-flex items-center gap-1 rounded-lg bg-brand-500 hover:bg-brand-600 active:scale-95 text-white px-2.5 py-1 text-[10px] font-bold transition shadow-sm"
                                        >
                                            <span>{t("Chat on WhatsApp")}</span>
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <footer className="border-t border-ink-100 px-5 py-3.5 bg-ink-50/50 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl border border-ink-200 bg-white hover:bg-ink-50 text-ink-700 px-4 py-2 text-xs font-bold transition active:scale-95 shadow-sm"
                    >
                        {t("Close")}
                    </button>
                </footer>
            </div>
        </div>
    );
}

